<template>
  <div class="stories-list-container">
    <!-- Skeleton loading state -->
    <div v-if="isLoading" class="stories-list">
      <div class="story-item skeleton" v-for="i in 5" :key="i">
        <div class="story-avatar-wrapper">
          <div class="story-avatar skeleton-avatar"></div>
        </div>
        <div class="story-name skeleton-text"></div>
      </div>
    </div>

    <!-- Stories list -->
    <div v-else class="stories-list" ref="listRef">
      <!-- Add Status Button -->
      <div class="story-item add-status" @click="showStatusOptions">
        <div class="story-avatar-wrapper add-button">
          <div class="story-avatar add-avatar">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="12" cy="12" r="1"></circle>
              <circle cx="19" cy="12" r="1"></circle>
              <circle cx="5" cy="12" r="1"></circle>
            </svg>
          </div>
        </div>
        <span class="story-name">{{ $t("stories.add_status") }}</span>
      </div>

      <!-- Status Groups -->
      <div
        v-for="(group, index) in statusGroups"
        :key="group.userId || index"
        class="story-item"
        @click="openViewer(index)"
      >
        <SegmentedCircleBorder
          :segment-count="group.statuses.length"
          :viewed-count="getViewedCount(group)"
          :size="60"
        >
          <!-- With Image -->
          <div v-if="group.user?.displayImage" class="story-avatar-wrapper">
            <div class="story-avatar">
              <img
                :src="group.user.displayImage"
                :alt="group.user?.displayName"
                @error="handleImageError"
              />
            </div>
          </div>
          <!-- Without Image - Simple circle with icon -->
          <div v-else class="story-avatar-wrapper user-placeholder">
            <i class="bi bi-person-fill"></i>
          </div>
        </SegmentedCircleBorder>
        <span class="story-name" :class="{ 'my-story-name': isMyStory(group) }">
          {{ getDisplayName(group) }}
        </span>
      </div>
    </div>

    <!-- Status Options Modal -->
    <div
      v-if="showOptions"
      class="status-options-overlay"
      @click.self="showOptions = false"
    >
      <div class="status-options-modal">
        <div class="option-item" @click="navigateToCreate">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          <span>{{ $t("stories.add_status") }}</span>
        </div>
        <div class="option-item" @click="navigateToHistory">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <polyline points="1 4 1 10 7 10"></polyline>
            <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path>
          </svg>
          <span>{{ $t("stories.my_statuses_history") }}</span>
        </div>
      </div>
    </div>

    <!-- Story Viewer Modal -->
    <Teleport to="body">
      <StoryViewer
        v-if="viewerOpen"
        :initial-group-index="selectedGroupIndex"
        @close="closeViewer"
      />
    </Teleport>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useStoriesStore } from "@/stores/stories";
import SegmentedCircleBorder from "./SegmentedCircleBorder.vue";
import StoryViewer from "./StoryViewer.vue";

const router = useRouter();
const store = useStoriesStore();
const { t } = useI18n();

// State
const showOptions = ref(false);
const viewerOpen = ref(false);
const selectedGroupIndex = ref(0);
const listRef = ref(null);

// Computed
const statusGroups = computed(() => store.statusGroups);
const isLoading = computed(() => store.isLoading);

// Methods
function getViewedCount(group) {
  return group.statuses.filter((s) => s.userHasViewed).length;
}

function isMyStory(group) {
  // Check if the display name is "Me" (set by normalizeUser when it's current user's story)
  const displayName = group.user?.displayName;
  return displayName === "Me" || group.user?.name === null;
}

function getDisplayName(group) {
  const displayName = group.user?.displayName;

  // If it's the current user's story
  if (
    displayName === "Me" ||
    (group.user && group.user.name === null && group.user.id !== 0)
  ) {
    return t("stories.my_story");
  }

  // If unknown user
  if (!displayName || displayName === "Unknown") {
    return t("stories.unknown_user");
  }

  return displayName;
}

function showStatusOptions() {
  showOptions.value = true;
}

function navigateToCreate() {
  showOptions.value = false;
  router.push("/status/create");
}

function navigateToHistory() {
  showOptions.value = false;
  router.push("/status/history");
}

function openViewer(index) {
  selectedGroupIndex.value = index;
  viewerOpen.value = true;
  // Prevent body scroll when viewer is open
  document.body.style.overflow = "hidden";
}

function closeViewer() {
  viewerOpen.value = false;
  document.body.style.overflow = "";
}

function handleImageError(event) {
  event.target.style.display = "none";
}

// Lifecycle
onMounted(() => {
  store.fetchStatuses();
});
</script>

<style scoped>
/* ========== Brand Color: #00c444 - Premium Stories Design ========== */
.stories-list-container {
  width: 100%;
  padding: 20px 0;
  background: linear-gradient(
    180deg,
    rgba(0, 196, 68, 0.05) 0%,
    rgba(0, 196, 68, 0.02) 50%,
    transparent 100%
  );
  border-radius: 24px;
  margin-bottom: 12px;
  position: relative;
  overflow: hidden;
}

.stories-list-container::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(0, 196, 68, 0.3),
    transparent
  );
}

.stories-list {
  display: flex;
  gap: 18px;
  overflow-x: auto;
  padding: 12px 24px;
  scrollbar-width: none;
  -ms-overflow-style: none;
  scroll-behavior: smooth;
}

.stories-list::-webkit-scrollbar {
  display: none;
}

.story-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  min-width: 80px;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative;
}

.story-item:hover {
  transform: translateY(-6px) scale(1.05);
}

.story-item:active {
  transform: scale(0.92);
}

.story-avatar-wrapper {
  width: 68px;
  height: 68px;
  border-radius: 50%;
  overflow: hidden;
  background: linear-gradient(145deg, #ffffff 0%, #f0fff5 100%);
  box-shadow: 0 4px 20px rgba(0, 196, 68, 0.15), 0 2px 8px rgba(0, 0, 0, 0.05),
    inset 0 -2px 6px rgba(0, 196, 68, 0.1);
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative;
}

.story-item:hover .story-avatar-wrapper {
  box-shadow: 0 8px 32px rgba(0, 196, 68, 0.25), 0 4px 12px rgba(0, 0, 0, 0.08),
    inset 0 -2px 8px rgba(0, 196, 68, 0.15);
}

.story-avatar-wrapper.add-button {
  background: linear-gradient(135deg, #00c444 0%, #00e050 50%, #00a43c 100%);
  box-shadow: 0 8px 28px rgba(0, 196, 68, 0.4), 0 4px 12px rgba(0, 196, 68, 0.2),
    inset 0 2px 4px rgba(255, 255, 255, 0.3);
  animation: pulse-glow 2.5s ease-in-out infinite;
}

@keyframes pulse-glow {
  0%,
  100% {
    box-shadow: 0 8px 28px rgba(0, 196, 68, 0.4),
      0 4px 12px rgba(0, 196, 68, 0.2), inset 0 2px 4px rgba(255, 255, 255, 0.3);
  }
  50% {
    box-shadow: 0 10px 35px rgba(0, 196, 68, 0.5),
      0 6px 18px rgba(0, 196, 68, 0.3), inset 0 2px 4px rgba(255, 255, 255, 0.4);
  }
}

.story-item:hover .story-avatar-wrapper.add-button {
  box-shadow: 0 12px 40px rgba(0, 196, 68, 0.5),
    0 6px 20px rgba(0, 196, 68, 0.3), inset 0 2px 6px rgba(255, 255, 255, 0.4);
  animation: none;
}

.story-avatar {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  overflow: hidden;
}

.story-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.story-item:hover .story-avatar img {
  transform: scale(1.1);
}

.add-avatar {
  color: #ffffff;
}

/* User Placeholder - Simple circle with centered icon */
.user-placeholder {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: linear-gradient(135deg, #e8fff1 0%, #d4f7e4 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 20px rgba(0, 196, 68, 0.2), 0 3px 10px rgba(0, 196, 68, 0.1);
}

.user-placeholder i {
  font-size: 32px;
  color: #00c444;
}

.story-item:hover .user-placeholder {
  box-shadow: 0 10px 30px rgba(0, 196, 68, 0.3),
    0 5px 15px rgba(0, 196, 68, 0.15);
}

.story-item:hover .user-placeholder i {
  transform: scale(1.1);
}

.story-name {
  font-size: 11px;
  font-weight: 600;
  color: #1a1a2e;
  text-align: center;
  max-width: 72px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: all 0.2s ease;
  letter-spacing: 0.2px;
}

.story-item:hover .story-name {
  color: #00c444;
}

/* My Story special styling */
.my-story-name {
  color: #00c444;
  font-weight: 700;
  background: linear-gradient(135deg, #00c444 0%, #00e050 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* Skeleton styles */
.skeleton .story-avatar-wrapper {
  background: linear-gradient(90deg, #e8fff1 25%, #d4f7e4 50%, #e8fff1 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
}

.skeleton-avatar {
  background: transparent;
}

.skeleton-text {
  width: 50px;
  height: 12px;
  border-radius: 6px;
  background: linear-gradient(90deg, #e8fff1 25%, #d4f7e4 50%, #e8fff1 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Status Options Modal */
.status-options-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease;
}

.status-options-modal {
  background: linear-gradient(145deg, #ffffff 0%, #f8fffe 100%);
  border-radius: 24px 24px 0 0;
  padding: 20px;
  width: 100%;
  max-width: 500px;
  animation: slideUp 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: 0 -10px 40px rgba(0, 196, 68, 0.15);
}

.status-options-modal::before {
  content: "";
  display: block;
  width: 40px;
  height: 4px;
  background: linear-gradient(90deg, #00c444, #00e050);
  border-radius: 2px;
  margin: 0 auto 20px;
}

.option-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 8px;
  background: transparent;
  border: 1px solid transparent;
}

.option-item:hover {
  background: linear-gradient(145deg, #e8fff1 0%, #d4f7e4 100%);
  border-color: rgba(0, 196, 68, 0.2);
  transform: translateX(4px);
}

.option-item:last-child {
  margin-bottom: 0;
}

.option-item svg {
  color: #00c444;
  transition: transform 0.3s ease;
}

.option-item:hover svg {
  transform: scale(1.15);
}

.option-item span {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a2e;
  transition: color 0.2s ease;
}

.option-item:hover span {
  color: #00c444;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Dark mode support */
[data-bs-theme="dark"] .stories-list-container,
[data-theme="dark"] .stories-list-container,
.dark .stories-list-container {
  background: linear-gradient(
    180deg,
    rgba(0, 196, 68, 0.1) 0%,
    rgba(0, 196, 68, 0.03) 50%,
    transparent 100%
  );
}

[data-bs-theme="dark"] .stories-list-container::before,
[data-theme="dark"] .stories-list-container::before,
.dark .stories-list-container::before {
  background: linear-gradient(
    90deg,
    transparent,
    rgba(0, 196, 68, 0.4),
    transparent
  );
}

[data-bs-theme="dark"] .story-avatar-wrapper,
[data-theme="dark"] .story-avatar-wrapper,
.dark .story-avatar-wrapper {
  background: linear-gradient(145deg, #2d2e30 0%, #242526 100%);
  box-shadow: 0 4px 20px rgba(0, 196, 68, 0.2), 0 2px 8px rgba(0, 0, 0, 0.3),
    inset 0 -2px 6px rgba(0, 196, 68, 0.15);
}

[data-bs-theme="dark"] .story-item:hover .story-avatar-wrapper,
[data-theme="dark"] .story-item:hover .story-avatar-wrapper,
.dark .story-item:hover .story-avatar-wrapper {
  box-shadow: 0 8px 32px rgba(0, 196, 68, 0.35), 0 4px 12px rgba(0, 0, 0, 0.4),
    inset 0 -2px 8px rgba(0, 196, 68, 0.2);
}

[data-bs-theme="dark"] .story-name,
[data-theme="dark"] .story-name,
.dark .story-name {
  color: #e4e6eb;
}

[data-bs-theme="dark"] .my-story-name,
[data-theme="dark"] .my-story-name,
.dark .my-story-name {
  background: linear-gradient(135deg, #00e050 0%, #00ff5a 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

[data-bs-theme="dark"] .user-placeholder,
[data-theme="dark"] .user-placeholder,
.dark .user-placeholder {
  background: linear-gradient(135deg, #1a3d2a 0%, #1e4d35 100%);
  box-shadow: 0 6px 20px rgba(0, 196, 68, 0.25), 0 3px 10px rgba(0, 0, 0, 0.3);
}

[data-bs-theme="dark"] .user-placeholder i,
[data-theme="dark"] .user-placeholder i,
.dark .user-placeholder i {
  color: #00e050;
}

[data-bs-theme="dark"] .story-item:hover .user-placeholder,
[data-theme="dark"] .story-item:hover .user-placeholder,
.dark .story-item:hover .user-placeholder {
  box-shadow: 0 10px 30px rgba(0, 196, 68, 0.35), 0 5px 15px rgba(0, 0, 0, 0.4);
}

[data-bs-theme="dark"] .status-options-modal,
[data-theme="dark"] .status-options-modal,
.dark .status-options-modal {
  background: linear-gradient(145deg, #242526 0%, #1a1b1d 100%);
}

[data-bs-theme="dark"] .option-item span,
[data-theme="dark"] .option-item span,
.dark .option-item span {
  color: #e4e6eb;
}

[data-bs-theme="dark"] .option-item:hover,
[data-theme="dark"] .option-item:hover,
.dark .option-item:hover {
  background: linear-gradient(
    145deg,
    rgba(0, 196, 68, 0.15) 0%,
    rgba(0, 196, 68, 0.08) 100%
  );
}

[data-bs-theme="dark"] .skeleton .story-avatar-wrapper,
[data-theme="dark"] .skeleton .story-avatar-wrapper,
.dark .skeleton .story-avatar-wrapper {
  background: linear-gradient(90deg, #2d2e30 25%, #3a3b3d 50%, #2d2e30 75%);
  background-size: 200% 100%;
}

[data-bs-theme="dark"] .skeleton-text,
[data-theme="dark"] .skeleton-text,
.dark .skeleton-text {
  background: linear-gradient(90deg, #2d2e30 25%, #3a3b3d 50%, #2d2e30 75%);
  background-size: 200% 100%;
}

/* RTL Support */
[dir="rtl"] .option-item:hover {
  transform: translateX(-4px);
}
</style>
